import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CatchingOnServicesImage from '@/media/images/CatchinOn_Ellie_Services.jpg';

const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

const staggerContainer = {
    animate: {
        transition: {
            staggerChildren: 0.1
        }
    }
};

export const Services = () => {
    const services = [
        { name: 'WEBSITE',          rotate:  6,  skew: -3  },
        { name: 'PAID SOCIAL',      rotate: -10, skew:  4  },
        { name: 'GRAPHICS',         rotate: -5,  skew: -5  },
        { name: 'ANALYTICS',        rotate:  4,  skew:  6  },
        { name: 'COPYWRITING',      rotate: -3,  skew: -4  },
        { name: 'CONTENT CREATION', rotate:  9,  skew:  3  },
        { name: 'EMAIL MARKETING',  rotate: -8,  skew: -6  },
        { name: 'NEWSLETTERS',      rotate:  11, skew:  5  },
    ];

    return (
        <div data-testid="services-page">
            {/* Hero + Services Section */}
            <section className="pt-40 pb-20 bg-brand-periwinkle px-6 md:px-12" data-testid="services-hero">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* Left: heading + text */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.7 }}
                        >
                            <h1 className="text-5xl lg:text-6xl font-black text-black uppercase leading-none mb-8">
                                <span className="bg-brand-lime px-2">WHAT</span> WE DO
                            </h1>
                            <p className="text-lg text-black mb-6 leading-relaxed">
                                Marketing shouldn't feel like guesswork.
                                Let's focus on the marketing that will actually move the needle.
                            </p>
                            <p className="text-lg text-black mb-6 leading-relaxed">
                                Whether you need help building a clear strategy, improving your
                                social media presence, or running campaigns that generate real results,
                                we're here to help.
                            </p>
                            <p className="text-lg text-black leading-relaxed">
                                We work with businesses that want practical advice, honest conversations,
                                and marketing support that fits their goals (and their budget).
                            </p>
                        </motion.div>

                        {/* Right: scattered rotated pills */}
                        <motion.div
                            initial="initial"
                            animate="animate"
                            variants={staggerContainer}
                            className="flex flex-wrap gap-6 justify-center items-center"
                            data-testid="services-grid"
                        >
                            {services.map((service) => (
                                <motion.span
                                    key={service.name}
                                    variants={fadeInUp}
                                    className="service-pill cursor-default"
                                    style={{ transform: `rotate(${service.rotate}deg) skewX(${service.skew}deg)` }}
                                    data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    {service.name}
                                </motion.span>
                            ))}
                        </motion.div>

                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 bg-white px-6 md:px-12" data-testid="pricing-section">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-4xl font-black text-black mb-6">Pricing</h2>
                            <p className="text-lg text-black/80 mb-6 leading-relaxed">
                                No two businesses are the same, so neither are our packages. Some clients 
                                need help with a one-off project, others want ongoing marketing support 
                                or a regular retainer.
                            </p>
                            <p className="text-lg text-black/80 mb-8 leading-relaxed">
                                We'll always recommend the approach that makes the most sense for your business.
                            </p>
                            <p className="text-lg text-black/80 mb-8">
                                Smaller projects start from <span className="font-bold text-2xl bg-brand-lime px-2">£100</span>, 
                                with bespoke packages and retainers available.
                            </p>
                            <Link to="/contact">
                                <button className="btn-lime" data-testid="get-quote-btn">
                                    GET A QUOTE
                                </button>
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6 }}
                            className="hidden lg:block"
                        >
                            <div className="full-arch aspect-[3/4] max-w-sm ml-auto">
                                <img
                                    src={CatchingOnServicesImage}
                                    alt="Creative workspace"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Previous Work Section */}
            <section className="py-20 bg-brand-periwinkle px-6 md:px-12" data-testid="previous-work">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                    >
                        <p className="text-sm font-bold tracking-widest text-black/60 mb-6">PREVIOUS WORK</p>
                        <div className="bg-white border-2 border-black p-8 max-w-xl">
                            <h3 className="text-2xl font-black text-black mb-4">ALTORITE</h3>
                            <p className="text-black/80 leading-relaxed">
                                We worked with Altorite to create their brand guidelines and support 
                                during their launch phase.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Services;
